import { useState } from 'react';
import { LuChevronRight } from 'react-icons/lu';
import { Button } from '@/components/Button';
import Input from '@/components/Input';
import { cn } from '@/lib/utils';

interface TrialStepFormProps {
  label: string;
  placeholder: string;
  value: string;
  type?: string;
  onChange: (value: string) => void;
  onNext: () => void;
  onBack?: () => void;
  /** 次へボタンのラベル。未指定時は「次へ」 */
  nextButtonLabel?: string;
  /** ボタン直下に表示する補足文言（例: 自動課金なし/クレジットカード登録不要） */
  textBelowButton?: string;
  validate?: (value: string) => string | null;
  externalError?: string | null;
  helperText?: string;
}

export default function TrialStepForm({
  label,
  placeholder,
  value,
  type = 'text',
  onChange,
  onNext,
  onBack,
  nextButtonLabel = '次へ',
  textBelowButton,
  validate,
  externalError,
  helperText,
}: TrialStepFormProps) {
  const [touched, setTouched] = useState(false);

  const validationError = externalError || (touched && validate ? validate(value) : null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (validate) {
      const err = validate(value);
      if (err) return;
    }
    onNext();
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-xs pt-xs md:gap-xl md:pt-md">
      <div className="flex flex-col gap-xs md:gap-sm">
        <label className="text-heading-xxs font-bold md:text-lg">{label}</label>
        <div>
          <Input
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className={cn(
              'h-[50px] w-full text-lg leading-[1.7] font-normal md:h-20',
              validationError && 'border-none outline-danger!'
            )}
            autoFocus
          />
          {helperText && !validationError && (
            <p className="mt-xs text-[10px] text-sub md:mt-sm md:text-xs">{helperText}</p>
          )}
          {validationError && (
            <p className="mt-xs text-[10px] text-danger md:mt-sm md:text-xs">{validationError}</p>
          )}
        </div>
      </div>

      <div className="flex flex-col">
        <div className="mb-md flex gap-[20px] md:mb-0 md:gap-8">
          {onBack && (
            <Button
              type="button"
              onClick={onBack}
              color="secondary"
              className="h-xxl w-[106px] shrink-0 text-[10px]! md:h-[65px] md:w-[144px] md:text-sm!"
            >
              戻る
            </Button>
          )}
          <Button
            type="submit"
            color="primary"
            disabled={!value.trim()}
            className={`h-xxl gap-2 text-[10px]! md:h-[65px] md:text-sm! ${
              onBack ? 'flex-1 shrink-0 md:w-[210px]' : 'flex-1'
            }`}
          >
            {nextButtonLabel}
            <LuChevronRight size={20} className="size-sm md:size-4" />
          </Button>
        </div>
        {textBelowButton && (
          <p className="mt-1 text-left text-[10px] leading-[150%] text-danger md:mt-1 md:text-xs">
            {textBelowButton}
          </p>
        )}
      </div>
    </form>
  );
}
